import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, OnInit, Optional, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AuthService, AuthErrorService, PnpLoginService, ILoginTwoFactorResendPayload, IRegistrationResponse, ILoginTwoFactorPayload, IUserPhoneNumberConfirmedPayload, PnpUserService, AuthErrors, IUserPhotoResponse } from '@pn-plus/pnp-services';
import { ITokenInfo2FA } from 'libs/pnp-services/src/lib/api/_models/pnp-token-info.model';
import { IAuthModel } from 'libs/pnp-services/src/lib/auth/_models/auth.model';
import { firstValueFrom, Observable, catchError, of, map, pipe } from 'rxjs';
import { IdentityVerificationComponent } from '../../core/components/identity-verification/identity-verification.component';
import { LoginService } from '../../core/services/login.service';

@Component({
    selector: 'pnl-verify-twofactor-phone',
    templateUrl: './verify-twofactor-phone.component.html',
    styleUrls: ['./verify-twofactor-phone.component.scss'],
})
export class VerifyTwofactorPhoneComponent implements OnInit {
    @ViewChild('identityVerification') identityVerificationRef !: IdentityVerificationComponent;
    
    public isLoading = false;
    public activeError: any;

    get phoneNumber(): string {
        const authModel = this.authService.getAuthModel();
        const twoFactorToken = authModel?.loginResponse?.twoFactorToken;
        return twoFactorToken?.userPhoneNumberLastFour || '****';
    }

    constructor(
        @Optional() @Inject(MAT_DIALOG_DATA) public authModel: IAuthModel,
        @Optional() public dialogRef: MatDialogRef<VerifyTwofactorPhoneComponent>,
        public authService: AuthService,
        public loginService: LoginService,
        private authErrorService: AuthErrorService,
        private pnpLoginService: PnpLoginService,
        private pnpUserService: PnpUserService
    ) {
        
    }

    getTwoFactorToken(): ITokenInfo2FA | undefined {
        const authModel = this.authService.getAuthModel();
        return authModel?.loginResponse?.twoFactorToken;
    }

    ngOnInit() {
    }

    async onCodeEntered(code: string) {
        this.setBusy(true);

        this.activeError = AuthErrors.Success;
        await firstValueFrom(this.verifyTwoFactor(code,  this.getTwoFactorToken()));
        await firstValueFrom(this.putHasPhoneNumber());
        await firstValueFrom(this.putRegistrationHasPhoneNumberConfirmed());
        await firstValueFrom(this.getUserRegistration());
        await firstValueFrom(this.getUser());
        await firstValueFrom(this.getUserPhoto());
        await this.loginService.storeBase64Avatar(this.authService.getAuthModel());
        
        if(!this.activeError) {
            this.loginService.navigateRegistration(this.authService.getAuthModel());
            this.dialogRef.close();
        }

        this.setBusy(false);
    }

    getUserRegistration(): Observable<IRegistrationResponse | null> {
        if(this.activeError) {
            return of(null);
        }
        
        return this.pnpUserService.getUserRegistration()
            .pipe(
                catchError(errorResponse => {
                    this.activeError = this.authErrorService.getErrorByType(errorResponse?.error?.type);
                    return of(null)
                }),
                map((response: any) => {
                    if(response) {
                        const authModel = this.authService.getAuthModel();
                        authModel.registrationResponse = response;
                        this.authService.updateAuthModel(authModel);
                    }
                    return response;
                })
            );
    }

    getUser(): Observable<IRegistrationResponse | null> {
        if(this.activeError) {
            return of(null);
        }
        
        return this.pnpUserService.getUser()
            .pipe(
                catchError(errorResponse => {
                    this.activeError = this.authErrorService.getErrorByType(errorResponse?.error?.type);
                    return of(null)
                }),
                map((response: any) => {
                    if(response) {
                        const authModel = this.authService.getAuthModel();
                        authModel.userResponse = response;
                        this.authService.updateAuthModel(authModel);
                    }
                    return response;
                })
            );
    }

    private getUserPhoto() {
        if(this.activeError) {
            return of(null);
        }

        return this.pnpUserService.getUserPhoto()
            .pipe(
                catchError((errorResponse: HttpErrorResponse) => {
                    this.activeError = this.authErrorService.getErrorByType(errorResponse?.error?.type);
                    return of(null);
                }),
                map((userPhotoResponse: IUserPhotoResponse | null) => {
                    const authModel = this.authService.getAuthModel();
                    if(userPhotoResponse){
                        authModel.userPhotoResponse = userPhotoResponse;
                        this.authService.updateAuthModel(authModel);
                    }
                    return userPhotoResponse;
                })
            );
    }

    putHasPhoneNumber(): Observable<any> {
        if(this.activeError) {
            return of(null);
        }

        return this.pnpUserService.putHasPhoneNumber()
            .pipe(
                catchError((errorResponse: HttpErrorResponse) => {
                    this.activeError = <any>this.authErrorService.getErrorByType(errorResponse?.error?.type);
                    return of(null);
                }),
                map((response) => {
                    return response;
                })
            );
    }

    putRegistrationHasPhoneNumberConfirmed(): Observable<any> {
        if(this.activeError) {
            return of(null);
        }
        return this.pnpUserService.putRegistrationHasPhoneNumberConfirmed()
            .pipe(
                catchError((errorResponse: HttpErrorResponse) => {
                    this.activeError = <any>this.authErrorService.getErrorByType(errorResponse?.error?.type);
                    return of(null);
                }),
                map((response) => {
                    return response;
                })
            );
    }

    verifyTwoFactor(code: string, token: ITokenInfo2FA | undefined): Observable<any> {
        if(this.activeError) {
            return of(null);
        }

        const payload: ILoginTwoFactorPayload = {
            twoFactorToken: <string>token?.jwt,
            code: code,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        }
        return this.pnpLoginService.postLoginTwoFactor(payload)
            .pipe(
                catchError((errorResponse: HttpErrorResponse) => {
                    this.activeError = this.authErrorService.getErrorByType(errorResponse?.error?.type).toString();
                    this.identityVerificationRef.reset();
                    return of(null);
                }),
                map((response: any) => {
                    const authModel = this.authService.getAuthModel();
                    if(response) {
                        authModel.loginResponse = response;
                        this.authService.updateAuthModel(authModel);
                    } else if(this.activeError) {
                        authModel.error = this.activeError;
                        this.authService.updateAuthModel(authModel);
                        if(this.activeError === AuthErrors.PhoneTaken || this.activeError === AuthErrors.UnexpectedError) {
                            this.loginService.navigate('/set-phone');
                        }
                    }
                    return response;
                })
            )
    }

    async onResendClicked($event: any) {
        const authModel = this.authService.getAuthModel();
        const token = this.getTwoFactorToken();
        if(token) {
            const resendToken = await firstValueFrom(await this.resend2FAConfirmationCode(token));
            if(resendToken?.jwt && authModel.loginResponse){
                authModel.loginResponse.twoFactorToken = resendToken; 
            }
        }
        this.authService.updateAuthModel(authModel);
    }
    
    resend2FAConfirmationCode(token: ITokenInfo2FA): Observable<any> {
        const payload: ILoginTwoFactorResendPayload = {
            twoFactorToken: token.jwt
        }
        return this.pnpLoginService.postLoginTwoFactorResend(payload)
            .pipe(
                catchError((errorResponse: HttpErrorResponse) => {
                    // TODO: Handle error here
                    this.activeError = this.authErrorService.getErrorByType(errorResponse?.error?.type);
                    return of(null);
                }),
                map((response) => {
                    return response;
                })
            );
    }
        
    onErrorAction($event: { error: number, $event: any}) {
        if(this.dialogRef) {
            this.dialogRef.close($event.error);
        }
        this.loginService.navigate('/');
    }

    setBusy(flag: boolean): void {
        this.isLoading = flag;
    }
}
